import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, Target, Users, Palette, Mic } from 'lucide-react';

interface TutorialProps {
  onClose: () => void;
}

const TUTORIAL_STEPS = [
  {
    icon: Target,
    title: "HIT 67 EXACTLY",
    description: "Your team's goal is to reach exactly 67 points. Not 66. Not 68. Exactly 67!",
    emoji: "🎯"
  },
  {
    icon: Users,
    title: "PICK YOUR NUMBER",
    description: "Each round, choose one of two numbers. Everyone picks at the same time - no talking!",
    emoji: "🔢"
  },
  {
    icon: Palette,
    title: "FOLLOW THE COLORS",
    description: "🔴 Red = too low, 🟡 Yellow = too high, 🟢 Green = getting close! Colors guide you without revealing the total.",
    emoji: "🎨"
  },
  {
    icon: Mic,
    title: "VOICE ACTIVATION",
    description: "When you hit 67, everyone must tap together within 2 seconds to win. Miss it and you lose!",
    emoji: "🗣️"
  }
];

export default function Tutorial({ onClose }: TutorialProps) {
  const [step, setStep] = useState(0);
  const isLastStep = step === TUTORIAL_STEPS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      localStorage.setItem('67chaos-tutorial-seen', 'true');
      onClose();
    } else {
      setStep(s => s + 1);
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const handleSkip = () => {
    localStorage.setItem('67chaos-tutorial-seen', 'true');
    onClose();
  };

  const currentStep = TUTORIAL_STEPS[step];
  const Icon = currentStep.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/90 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card rounded-3xl p-8 max-w-md w-full space-y-6 relative"
      >
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Progress dots */}
        <div className="flex justify-center gap-2">
          {TUTORIAL_STEPS.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
            className="text-center space-y-4"
          >
            <div className="text-6xl">{currentStep.emoji}</div>
            <div className="inline-flex p-4 rounded-2xl bg-primary/20">
              <Icon className="h-12 w-12 text-primary" />
            </div>
            <h2 className="text-2xl font-black text-foreground">{currentStep.title}</h2>
            <p className="text-muted-foreground font-medium leading-relaxed">
              {currentStep.description}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-4">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={step === 0}
            className="font-bold"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            className="font-bold glow-green"
          >
            {isLastStep ? "LET'S GO!" : "Next"}
            {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
